import { useState, useMemo } from 'react'
import {
  useSortable,
} from '@dnd-kit/sortable';
import { MoreOutlined, MenuOutlined } from '@ant-design/icons'
import { Input, Tooltip } from 'antd'
import { 
  MultipleListsPopover,
} from 'src/DigitalProtocolModule/components'
import { useSelector } from 'react-redux';
import { fetchHistory } from 'src/actions/historyTrial';
import { useFootnoteHooks } from 'src/DigitalProtocolModule/hooks';
import { FOOTNOTE_GROUP } from 'src/DigitalProtocolModule/constants';
import { ActivityFootnoteTooltip } from '../ActivityFootnoteTooltip';
import talkIcon from "src/assets/svgs/icon-talk.svg"
import styles from './index.module.scss'

const period_items:Digital_Protocol_SoA_Table_Data.PopoverItemsList[] = [  
{ name: 'Period Type', icon: 'AppstoreAddOutlined', color: 'black' },  
{ name: 'Add Period', icon: 'PlusCircleOutlined', color: 'black' },  
{ name: 'Delete Period', icon: 'MinusCircleOutlined', color: 'black' },  
{ name: 'Reset Period', icon: 'ClearOutlined', color: 'black' },  
{ name: 'Duplicate Period', icon: 'CopyOutlined', color: 'black' },  
{ name: 'Add Footnote', icon: 'MessageOutlined', color: 'black' },  
{ name: 'Move Period', icon: 'MenuOutlined', color: 'black' },  
]; 

const colorList: Digital_Protocol_SoA_Table_Data.ColorListItem[] = [
  {
    visit_type_id: 1,
    visit_type: "Screening Visits",
    color: "#FFB600",
  },
  {
    visit_type_id: 2,
    visit_type: "Treatment Visits",
    color: "#EB8C00",
  },
  {
    visit_type_id: 3,
    visit_type: "Follow-up Visits",
    color: "#295477",
  },
];

interface DraggablePeriodProps {
  periodItem: Digital_Protocol_SoA_Table_Data.PeriodData;
  idx: number;
  onAction: (name: string, idx: number) => void;
  key:number
  whetherPeriodDraggable: number
  periodNameChangeEvt: (e: any, periodId: number, periodName: string) => void
  visitType: Digital_Protocol_SoA_Table_Data.VisitType[]
  visitChangeHandler: (e: any, periodId: number) => void
  editPeriodFootnoteHandler: (period: Digital_Protocol_SoA_Table_Data.PeriodData, footnote_type_id: number) => void
  isReadOnly?: boolean;
}

export function DraggablePeriod({
  idx,
  key,
  periodItem,
  whetherPeriodDraggable,
  periodNameChangeEvt,
  onAction,
  visitType,
  visitChangeHandler,
  editPeriodFootnoteHandler,
  isReadOnly = false,
}: DraggablePeriodProps) {
  // get footnote config
  const footnoteConfigState = useSelector(fetchHistory).data.dpFootnoteConfig;

  const { getFootnoteStateMergedWithSoftSave, doFootnoteSoftDelete } = useFootnoteHooks()

  const [footnoteMergedDataState] = useState(getFootnoteStateMergedWithSoftSave())
  const [isEdit, setIsEdit] = useState(false)

  const periodFootnoteList: DPFootnote.FootnoteData[] = useMemo(() => {
    return footnoteMergedDataState?.filter(
      (item: DPFootnote.FootnoteData) =>
        item?.footnote_group === FOOTNOTE_GROUP.PERIOD &&
        item?.period_id === periodItem.period_id
      ).map((item: DPFootnote.FootnoteData) => {
        const footnoteType = footnoteConfigState.footnoteTypeList.find(typeItem => typeItem.footnote_type_id === item.footnote_type_id)
        return {
          ...item,
          footnote_type: footnoteType?.footnote_type
        }
      });
  }, [footnoteConfigState, footnoteMergedDataState]);

  const getHeaderStyles = (item) => {
      let content = item?.no_of_visits;
      let width = 1;
      width = content > 0 ? content : 1;
      const style = {
        fontSize: width !== 1 ? "14px" : "12px",
        cursor: `${whetherPeriodDraggable !== item.period_id? 'default' : 'move'}`,
        flex: `${width} 0`,
        minWidth: width !== 1 ? `${41 * width}px` : "41px",
        touchAction: "none",
        display: "flex",
        justifyContent: "center",
        lineHeight: "15px",
        border: `3px solid ${
          colorList[
            colorList.findIndex((i) => i.visit_type_id === item.visit_type_id)
          ]?.color
        }`,
        margin: "0 1px 0 1px",
        alignItems: "center",
      };
      return style;
    };


    const {
      attributes,
      listeners,
      setNodeRef,
    } = 
      useSortable({
          id: periodItem.period_id, 
          disabled: whetherPeriodDraggable !== periodItem.period_id
      });

    const handleEditInFootnoteTooltipForPeriod = (footnote_type_id) => {
      editPeriodFootnoteHandler(periodItem, footnote_type_id)
    }

    return (
      <div 
          style={getHeaderStyles(periodItem)} 
          key={key} 
          ref={setNodeRef}
          {...attributes} {...listeners}
      >
          <MenuOutlined style={{fontSize: '11px',marginTop: '3px', marginLeft: '5px', marginRight: '10px', color: `${whetherPeriodDraggable !== periodItem.period_id ? 'transparent' : 'black'}`, display: `${whetherPeriodDraggable !== periodItem.period_id ? 'none' : 'block'}`
        }}/> 
          
          {isEdit ? (
            <Input className="td cell-input periodNameInput" 
              style={{fontSize: "14px",
                  textAlign: 'center'
              }} 
              key={`period_${periodItem?.period_id}`} 
              defaultValue={periodItem.period_name}  
              onBlur={(e) => {
                periodNameChangeEvt(e, periodItem.period_id, periodItem.period_name)
                setIsEdit(false)
              }}
              autoFocus
              maxLength={500}
              readOnly={isReadOnly}
            />  
          ): (
            <div className={styles.labelWrapper}>
              {periodFootnoteList?.length > 0 && (
                <Tooltip title={
                  <ActivityFootnoteTooltip
                    footnoteList={periodFootnoteList}
                    isReadOnly={isReadOnly}
                    onEdit={handleEditInFootnoteTooltipForPeriod}
                    onDel={doFootnoteSoftDelete}/>
                  }
                  overlayInnerStyle={{whiteSpace: "pre-wrap", borderRadius: 8, backdropFilter: "blur(3px)"}}
                  overlayClassName="footnote-tooltip"
                  placement="topLeft"
                  arrowPointAtCenter={true}
                  zIndex={999}
                  >
                  <img src={talkIcon} className={styles.talkIcon} alt="" />
                </Tooltip>
              )}
              <Tooltip title={periodItem.period_name || 'Click to edit'}  placement="bottom" overlayStyle={{ pointerEvents: 'none' }} >
                <span className={styles.label} onClick={() => setIsEdit(true)}>{periodItem.period_name}</span>
              </Tooltip>
              
            </div>
          )}

          {!isReadOnly && (
            <MultipleListsPopover items={period_items} onAction={onAction} idx={periodItem.period_id} period={periodItem} visitType={visitType} visitChangeHandler={visitChangeHandler}>
              <MoreOutlined />
            </MultipleListsPopover>
          )}
    </div>
  )
}